import { DiscountScope } from '@squareup/sq-addons-web-sdk';

import { MenuItemsConverter } from 'converters/menu-items-converter';
import { DiscountItemsConverter } from 'converters/discount-items-converter';
import { ServiceChargeItemsConverter } from 'converters/service-charge-items-converter';
import { TenderItemsConverter } from 'converters/tender-items-converter';
import { isDiscountUpdated } from 'store/api/discounts/utils/discountUtil';
import { calculateSubTotal } from './subTotalUtil';

/**
 * Builds a Paytronix Check from a Square Order. The Check is sent to Paytronix to process discounts.
 * @param {Order} order - The Square Order to convert to a Paytronix Check.
 * @returns {Check} A Paytronix Check.
 */
export function buildPxCheck(order, cardNumber, excludeDiscountId = null) {
  // Filter out non-Paytronix discounts.
  const paytronixDiscounts = (order?.discounts ?? []).filter(
    (discount) => !!discount.metadata?.pxId
  );

  const validPaytronixDiscounts = paytronixDiscounts.filter(
    (discount) =>
      discount.uid === excludeDiscountId || !isDiscountUpdated(discount)
  );

  return {
    header: {
      number: order.id,
      isReturn: false,
      openTime: order.createdAt,
    },
    items: [
      MenuItemsConverter.squareToPaytronix(
        order.lineItems ?? [],
        validPaytronixDiscounts.filter(
          (discount) => discount.scope === DiscountScope.LINE_ITEM
        ),
        cardNumber
      ),
      DiscountItemsConverter.squareToPaytronix(
        validPaytronixDiscounts.filter(
          (discount) => discount.scope !== DiscountScope.LINE_ITEM
        ),
        cardNumber
      ),
      ServiceChargeItemsConverter.squareToPaytronix(
        order?.serviceCharges ?? []
      ),
      TenderItemsConverter.squareToPaytronix(order?.tenders ?? []),
    ].flat(),
    totals: {
      subTotal: calculateSubTotal(order),
      taxTotal: Number(order.totalTaxMoney?.amount ?? 0) / 100,
      discountTotal: (-1 * Number(order.totalDiscountMoney?.amount ?? 0)) / 100,
      serviceChargeTotal:
        Number(order.totalServiceChargeMoney?.amount ?? 0) / 100,
      tipTotal: Number(order.totalTipMoney?.amount ?? 0) / 100,
      checkTotal:
        (Number(order.totalMoney?.amount ?? 0) -
          Number(order.totalTipMoney?.amount ?? 0)) /
        100,
      tenderTotal:
        (order?.tenders ?? []).reduce(
          (accumulator, tender) =>
            accumulator + Number(tender.amountMoney?.amount ?? 0),
          0
        ) / 100,
      itemizedTotals: [],
    },
  };
}
